/*
 * File: SelectMedium.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on April 20, 2025 at 11:54 PM
 * Copyright © 2025 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: April 28, 2025 at 3:42 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, { useEffect, forwardRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import { Parser, ProcessNodeDefinitions } from 'html-to-react';

// Utilities
import { isValidNode, parseMarkdownLinks } from '../../../utilities/parser';
import { toastMessage } from '../../../utilities/toaster';

// Components
import { Typography } from '../../Typography';
import { Button } from '../../Button';
import { LocaleLink } from '../../LocaleLink';

// Constants
import { AVAILABLE_I18N_NAMESPACES } from '../../../../constants/base.constants';

// Styles
import * as S from './SelectMedium.styles';


/**
 * Constants
 */

export const STORY_MEDIUM_OPTIONS = {
	TEXT: 'text',
	IMAGE: 'image'
};


/**
 * Configuration
 */

// Parser
const ReactParser = new Parser();

// Node Definitions
const processNodeDefinitions = new ProcessNodeDefinitions();

// Parsing Instructions
const ParsingInstructions = [
	{
		shouldProcessNode(node) {
			return node.name === 'a';
		},
		processNode(node, children) {
			return <LocaleLink key={node.attribs.href} to={node.attribs.href} target="_blank">{children}</LocaleLink>;
		}
	},
	{
		shouldProcessNode() { return true; },
		processNode: processNodeDefinitions.processDefaultNode
	}
];


/**
 * Component
 */

export const SelectMedium = forwardRef(({
	className, moveForward, moveBackward, selectedMedium, setSelectedMedium, context
}, ref) => {

	// Get current partner from hook
	const partner = useSelector((state) => state.partner.value);

	// Get current locale from hook
	const locale = useSelector((state) => state.locale.value);

	// Get UI mode from hook
	const uiMode = useSelector((state) => state.ui.value);

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Get partner content
	const partnerContent = useMemo(() => partner?.content?.pages?.share_story?.components?.select_medium, [partner]);

	// Handle next action
	const nextAction = () => {

		// Validate selected medium
		if (!selectedMedium) {
			toastMessage(uiMode, partner, t('pageShareStory.selectMedium.errorMessage'));
			return;
		}

		// Move to next step
		moveForward();
	};

	// Perform actions on component load
	useEffect(() => {}, []);

	// Return component
	return (
		<S.StepContainer ref={ref} className={className}>
			<S.InnerStep>

				{/* Content Container */}
				<S.ContentContainer>

					{/* Title */}
					{context === 'story' ? (
						<Typography tag="h1" weight="black">
							{!partnerContent?.title ? (
								<Trans t={t} i18nKey="pageShareStory.selectMedium.titleStory">
									Share your story.
								</Trans>
							) : ReactParser.parseWithInstructions(parseMarkdownLinks(partnerContent?.title[locale.localeBaseId]), isValidNode, ParsingInstructions)}
						</Typography>
					) : (
						<Typography tag="h1" weight="black">
							{!partnerContent?.title ? (
								<Trans t={t} i18nKey="pageShareStory.selectMedium.titleUpdate">
									Share an update.
								</Trans>
							) : ReactParser.parseWithInstructions(parseMarkdownLinks(partnerContent?.title[locale.localeBaseId]), isValidNode, ParsingInstructions)}
						</Typography>
					)}

					{/* Subtitle */}
					<Typography tag="p">
						{!partnerContent?.subtitle ? (
							<Trans t={t} i18nKey="pageShareStory.selectMedium.subtitle">
								We each express ourselves differently, some in the form of writing and others in the form of artwork. Please select which method you would like express yourself with.
							</Trans>
						) : ReactParser.parseWithInstructions(parseMarkdownLinks(partnerContent?.subtitle[locale.localeBaseId]), isValidNode, ParsingInstructions)}
					</Typography>

				</S.ContentContainer>

				{/* Medium Options */}
				<S.MediumOptions>

					{/* Narrative Option */ }
					<S.MediumOption className="animate" onClick={() => { setSelectedMedium(STORY_MEDIUM_OPTIONS.TEXT); }}>
						<S.OptionCard>
							<S.ActiveIndicator className="animate" $isActive={selectedMedium === STORY_MEDIUM_OPTIONS.TEXT} />
							<S.Illustration src={`${process.env.CDN_URL}/public/assets/story-text-option-illustration.png`} />
							<Typography tag="h4" weight="bold">
								<Trans t={t} i18nKey="pageShareStory.selectMedium.narrativeOptionTitle">
									Share Narrative
								</Trans>
							</Typography>
							<Typography tag="p" variation="2">
								{!partnerContent?.narrative_subtitle ? (
									<Trans t={t} i18nKey="pageShareStory.selectMedium.narrativeOptionSubtitle">
										Share your experience or what healing means to you through written words. It can be as long or short as you want.
									</Trans>
								) : partnerContent?.narrative_subtitle[locale.localeBaseId]}
							</Typography>
						</S.OptionCard>
					</S.MediumOption>

					{/* Artwork Option */ }
					<S.MediumOption className="animate" onClick={() => { setSelectedMedium(STORY_MEDIUM_OPTIONS.IMAGE); }}>
						<S.OptionCard>
							<S.ActiveIndicator className="animate" $isActive={selectedMedium === STORY_MEDIUM_OPTIONS.IMAGE} />
							<S.Illustration src={`${process.env.CDN_URL}/public/assets/story-image-option-illustration.png`} />
							<Typography tag="h4" weight="bold">
								<Trans t={t} i18nKey="pageShareStory.selectMedium.artworkOptionTitle">
									Share Artwork
								</Trans>
							</Typography>
							<Typography tag="p" variation="2">
								{!partnerContent?.artwork_subtitle ? (
									<Trans t={t} i18nKey="pageShareStory.selectMedium.artworkOptionSubtitle">
										Share your experience or what healing means to you through visual media (artwork, photography).
									</Trans>
								) : partnerContent?.artwork_subtitle[locale.localeBaseId]}
							</Typography>
						</S.OptionCard>
					</S.MediumOption>

				</S.MediumOptions>

				{/* Continue Button */}
				<S.ActionContainer>
					<Button size="large" variant="text" onClick={moveBackward}>
						<FontAwesomeIcon icon={['fas', 'arrow-left-long']} />
						<Typography>
							<Trans t={t} i18nKey="actions.back">
								Back
							</Trans>
						</Typography>
					</Button>
					<Button size="large" onClick={nextAction}>
						<Typography>
							<Trans t={t} i18nKey="actions.continue">
								Continue
							</Trans>
						</Typography>
						<FontAwesomeIcon icon={['fas', 'arrow-right-long']} />
					</Button>
				</S.ActionContainer>

				{/* Legal Snippet */}
				<S.LegalSnippet />

			</S.InnerStep>
		</S.StepContainer>
	);
});


/**
 * Configuration
 */

SelectMedium.displayName = 'SelectMedium';
SelectMedium.propTypes = {
	className: PropTypes.string,
	moveForward: PropTypes.func,
	moveBackward: PropTypes.func,
	selectedMedium: PropTypes.string,
	setSelectedMedium: PropTypes.func,
	context: PropTypes.string
};
SelectMedium.defaultProps = {
	className: null,
	moveForward: null,
	moveBackward: null,
	selectedMedium: null,
	setSelectedMedium: null,
	context: 'story'
};
